/**
 * Coordinator Journey Planner — M16B-07D-B1
 * ==========================================
 *
 * Spreadsheet-style operational planning grid wired to the canonical
 * Patient Case Plan stack:
 *
 *   GET  /api/clinic/cases/:caseId/planner-context
 *   GET  /api/clinic/cases/:caseId/plan
 *   POST /api/clinic/cases/:caseId/materialize-plan
 *   POST /api/clinic/cases/:caseId/plan/bulk-overrides
 *   GET  /api/clinic/cases/:caseId/plan/revisions
 *   GET  /api/clinic/cases/:caseId/assignable-team
 *
 * Rules honoured (M16B-07D-B1 brief):
 *   • One screen — no template / runtime / before / after splits.
 *   • Row source visible as a column, never as separate screens.
 *   • Dense editable table (NOT cards).
 *   • Payment precondition blocks planning until a paid payment exists.
 *   • Role + Assignee are dropdowns; assignee filtered by selected role.
 *   • Saving writes overrides only — original template and accepted
 *     offer are NOT mutated.
 *   • No new domain, no new workflow engine, no Before/After categories,
 *     no Booking / Travel / Arrival / Transfer / Accommodation domains.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown,
  ArrowUp,
  Copy as CopyIcon,
  Loader2,
  Lock,
  Plus,
  RefreshCw,
  Save,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

import { api } from '../../utils/api';

const ROLE_KEYS = [
  { value: 'Doctor', labelKey: 'provider.journeyPlanner.roleDoctor' },
  { value: 'Coordinator', labelKey: 'provider.journeyPlanner.roleCoordinator' },
  { value: 'After Sales', labelKey: 'provider.journeyPlanner.roleAfterSales' },
  { value: 'Sales', labelKey: 'provider.journeyPlanner.roleSales' },
  { value: 'Patient', labelKey: 'provider.journeyPlanner.rolePatient' },
];
const ROW_TYPE_KEYS = [
  { value: 'requirement', labelKey: 'provider.journeyPlanner.typeRequirement' },
  { value: 'task', labelKey: 'provider.journeyPlanner.typeTask' },
];
const PRIORITY_KEYS = [
  { value: 'low', labelKey: 'provider.journeyPlanner.prioLow' },
  { value: 'normal', labelKey: 'provider.journeyPlanner.prioNormal' },
  { value: 'high', labelKey: 'provider.journeyPlanner.prioHigh' },
  { value: 'urgent', labelKey: 'provider.journeyPlanner.prioUrgent' },
];

const SOURCE_BADGE = {
  template: 'bg-sky-100 text-sky-800 border-sky-200',
  offer: 'bg-violet-100 text-violet-800 border-violet-200',
  campaign: 'bg-amber-100 text-amber-800 border-amber-200',
  override: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  manual: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

const SOURCE_LABEL = {
  template: 'treatment_template',
  offer: 'offer_item',
  campaign: 'campaign',
  override: 'manual',
  manual: 'manual',
};

function uuid() {
  return `man_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
}

function normaliseRow(r) {
  // Defensive normalisation so the grid never crashes on missing fields.
  return {
    id: r.id,
    source: r.source || 'manual',
    step_id: r.step_id || '',
    kind: r.kind || 'task',
    name: r.name || '',
    patient_action: r.patient_action || '',
    requirement_id: r.requirement_id || '',
    requirement_type: r.requirement_type || '',
    document_required: !!r.document_required,
    consent_required: !!r.consent_required,
    default_role: r.default_role || '',
    default_assignee: r.default_assignee || '',
    due_offset_days:
      r.due_offset_days === null || r.due_offset_days === undefined
        ? ''
        : r.due_offset_days,
    due_date: r.due_date || '',
    priority: r.priority || 'normal',
    notes: r.notes || '',
    status: r.status || 'active',
    row_order: r.row_order === undefined || r.row_order === null ? null : r.row_order,
    satisfaction_status: r.satisfaction_status || null,
    modified_by_override: !!r.modified_by_override,
    // Provenance (read-only)
    source_offer_id: r.source_offer_id,
    source_campaign_id: r.source_campaign_id,
    source_template_id: r.source_template_id,
    source_override_id: r.source_override_id,
  };
}

export default function CoordinatorJourneyPlanner() {
  const { caseId } = useParams();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [materializing, setMaterializing] = useState(false);
  const [error, setError] = useState(null);

  const [ctx, setCtx] = useState(null);
  const [plan, setPlan] = useState(null);
  const [team, setTeam] = useState([]);
  const [rows, setRows] = useState([]);
  const [steps, setSteps] = useState([]);
  // Pending op buffer — each user edit appends an override op; Save flushes.
  const [pendingOps, setPendingOps] = useState([]);
  // M16B-07D-B1-HF — per-row pending-state classification so the
  // coordinator can see at a glance what they've changed before Save.
  //   added   → green left border + ADD chip
  //   edited  → amber left border + EDIT chip
  //   deleted → muted strikethrough + red left border + DELETE chip
  //   moved   → indigo left border + MOVED chip
  const [rowState, setRowState] = useState({});
  // Soft-deleted row ids — we keep them visible until Save so the
  // coordinator can undo by re-adding.  After Save they are persisted as
  // remove_row overrides and the reload drops them.
  const [pendingDeletes, setPendingDeletes] = useState(new Set());
  const [filterSource, setFilterSource] = useState('all');

  // ─── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ctxRes, teamRes, planRes] = await Promise.all([
        api.get(`/clinic/cases/${caseId}/planner-context`),
        api.get(`/clinic/cases/${caseId}/assignable-team`).catch(() => ({ data: { roles: [] } })),
        api.get(`/clinic/cases/${caseId}/plan`).catch((e) => ({ data: e?.response?.data || null })),
      ]);
      setCtx(ctxRes.data);
      setTeam(teamRes.data?.roles || []);
      const planDoc = planRes.data?.patient_case_plan || null;
      setPlan(planDoc);
      if (planDoc?.live_plan) {
        setRows((planDoc.live_plan.rows || []).map(normaliseRow));
        setSteps(planDoc.live_plan.journey_steps || []);
      } else {
        setRows([]);
        setSteps([]);
      }
      setPendingOps([]);
      setRowState({});
      setPendingDeletes(new Set());
    } catch (e) {
      const detail = e?.response?.data;
      setError(detail?.message || detail?.error || e.message || t('provider.journeyPlanner.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    if (caseId) load();
  }, [caseId, load]);

  // ─── Materialize ──────────────────────────────────────────────
  const materializeNow = async () => {
    if (!ctx?.treatment_slug) {
      toast.error(t('provider.journeyPlanner.toastNoSlug'));
      return;
    }
    setMaterializing(true);
    try {
      const { data } = await api.post(`/clinic/cases/${caseId}/materialize-plan`, {
        treatment_slug: ctx.treatment_slug,
        offer_adjustments: ctx.offer_adjustments_preview || [],
      });
      if (data?.success) {
        toast.success(t('provider.journeyPlanner.toastMaterialized'));
        await load();
      } else {
        toast.error(data?.message || t('provider.journeyPlanner.toastMaterializeFailed'));
      }
    } catch (e) {
      const detail = e?.response?.data;
      toast.error(detail?.message || detail?.error || e.message || t('provider.journeyPlanner.toastMaterializeFailed'));
    } finally {
      setMaterializing(false);
    }
  };

  // ─── Mutators ─────────────────────────────────────────────────
  const queueOp = (op) => setPendingOps((prev) => [...prev, op]);

  const markRowState = (id, kind) => {
    setRowState((prev) => {
      const cur = prev[id];
      // "added" sticks — even if you also edit, it's still an addition.
      if (cur === 'added') return prev;
      // "deleted" overrides everything else visually.
      if (kind === 'deleted') return { ...prev, [id]: 'deleted' };
      if (cur === 'deleted' && kind !== 'deleted') return prev;
      return { ...prev, [id]: kind };
    });
  };

  const updateRowLocal = (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch, modified_by_override: true } : r)));
  };

  const onEditRow = (id, field, value) => {
    // role change clears assignee if it no longer matches that role
    let patch = { [field]: value };
    if (field === 'default_role') {
      patch.default_assignee = '';
    }
    updateRowLocal(id, patch);
    markRowState(id, 'edited');
    queueOp({ op: 'edit_row', args: { row_id: id, patch } });
  };

  const onDeleteRow = (id) => {
    // Soft delete — keep visible (strikethrough) until Save.  This lets
    // the coordinator see what they're removing and reconsider before
    // committing.
    setPendingDeletes((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    markRowState(id, 'deleted');
    queueOp({ op: 'remove_row', args: { row_id: id } });
  };

  const onUndoDelete = (id) => {
    setPendingDeletes((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setRowState((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setPendingOps((prev) => prev.filter((o) => !(o.op === 'remove_row' && o.args?.row_id === id)));
  };

  const onDuplicateRow = (id) => {
    const src = rows.find((r) => r.id === id);
    if (!src) return;
    const newId = uuid();
    const newRow = { ...src, id: newId, source: 'override', modified_by_override: true };
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.id === id);
      const next = [...prev];
      next.splice(idx + 1, 0, newRow);
      return next;
    });
    markRowState(newId, 'added');
    queueOp({ op: 'add_row', args: { row: { ...stripUiFields(newRow) } } });
  };

  const onAddManualRow = () => {
    const newId = uuid();
    const firstStep = steps.length > 0
      ? [...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0]
      : null;
    if (!firstStep) {
      toast.error(t('provider.journeyPlanner.toastAddStepPrereq'));
      return;
    }
    const newRow = normaliseRow({
      id: newId,
      source: 'override',
      step_id: firstStep.id,
      kind: 'task',
      name: t('provider.journeyPlanner.defaultRowName'),
      default_role: '',
      default_assignee: '',
      priority: 'normal',
      status: 'active',
    });
    setRows((prev) => [...prev, newRow]);
    markRowState(newId, 'added');
    queueOp({ op: 'add_row', args: { row: { ...stripUiFields(newRow) } } });
  };

  const onAddManualStep = () => {
    const name = window.prompt(t('provider.journeyPlanner.promptNewStepName'));
    if (!name) return;
    const id = `js_${Math.random().toString(36).slice(2, 10)}`;
    const order = (steps[steps.length - 1]?.order ?? -1) + 1;
    const newStep = { id, name, order, archived: false };
    setSteps((prev) => [...prev, newStep]);
    queueOp({ op: 'add_step', args: { step: newStep } });
  };

  const onMoveRow = (id, dir) => {
    // M16B-07D-B3 — row reorder is now PERSISTED via edit_row.row_order
    // overrides instead of pure in-memory shuffle.  The full ordered list
    // of rows in the affected step is renumbered in 10-unit increments so
    // future moves have headroom without renumbering everything again.
    const target = rows.find((r) => r.id === id);
    if (!target) return;
    const stepId = target.step_id;
    const stepSiblings = visibleRows.filter((r) => r.step_id === stepId);
    const idx = stepSiblings.findIndex((r) => r.id === id);
    if (idx < 0) return;
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= stepSiblings.length) return;
    // Compute the new ordering after swap.
    const newSequence = [...stepSiblings];
    [newSequence[idx], newSequence[swap]] = [newSequence[swap], newSequence[idx]];
    // Renumber every sibling so the order is durable + monotonic.
    const updates = newSequence.map((r, i) => ({
      row_id: r.id,
      row_order: (i + 1) * 10,
    }));
    setRows((prev) => prev.map((r) => {
      const u = updates.find((x) => x.row_id === r.id);
      return u ? { ...r, row_order: u.row_order, modified_by_override: true } : r;
    }));
    // Queue ops OUTSIDE the setRows updater so React StrictMode's
    // double-invocation does not duplicate them.
    for (const u of updates) {
      queueOp({ op: 'edit_row', args: { row_id: u.row_id, patch: { row_order: u.row_order } } });
    }
    markRowState(id, 'moved');
  };

  const onMoveStep = (id, dir) => {
    // M16B-07D-B3 — fix step reorder by moving queueOp OUTSIDE the
    // setSteps updater (React StrictMode double-invocation was causing
    // each click to queue 4 swap ops instead of 2 → net no-op after Save).
    const sorted = [...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const idx = sorted.findIndex((s) => s.id === id);
    if (idx < 0) return;
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= sorted.length) return;
    const a = sorted[idx];
    const b = sorted[swap];
    setSteps((prev) => prev.map((s) => {
      if (s.id === a.id) return { ...s, order: b.order };
      if (s.id === b.id) return { ...s, order: a.order };
      return s;
    }));
    queueOp({ op: 'edit_step', args: { step_id: a.id, patch: { order: b.order } } });
    queueOp({ op: 'edit_step', args: { step_id: b.id, patch: { order: a.order } } });
  };

  const onSave = async () => {
    if (pendingOps.length === 0) {
      toast.info(t('provider.journeyPlanner.toastNoPendingChanges'));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/clinic/cases/${caseId}/plan/bulk-overrides`, {
        ops: pendingOps,
      });
      if (data?.success) {
        toast.success(t('provider.journeyPlanner.toastSaved', { count: data.applied }));
        await load();
      } else {
        toast.error(data?.message || t('provider.journeyPlanner.toastSaveFailed'));
      }
    } catch (e) {
      const detail = e?.response?.data;
      toast.error(detail?.message || detail?.error || e.message || t('provider.journeyPlanner.toastSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // ─── Derived ──────────────────────────────────────────────────
  // (M16B-07D-B3) All these derivations are tiny pure expressions over
  // state; React Compiler auto-memoises them.  Manual useMemo here
  // triggers the `react-hooks/preserve-manual-memoization` blocking rule.
  const sortedSteps = [...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const visibleRows = (() => {
    const base = filterSource === 'all'
      ? rows
      : rows.filter((r) => SOURCE_LABEL[r.source] === filterSource);
    const arrayIdx = Object.fromEntries(base.map((r, i) => [r.id, i]));
    const stepOrder = Object.fromEntries(sortedSteps.map((s) => [s.id, s.order ?? 999]));
    return [...base].sort((a, b) => {
      const oa = stepOrder[a.step_id] ?? 999;
      const ob = stepOrder[b.step_id] ?? 999;
      if (oa !== ob) return oa - ob;
      const ra = a.row_order ?? Number.POSITIVE_INFINITY;
      const rb = b.row_order ?? Number.POSITIVE_INFINITY;
      if (ra !== rb) return ra - rb;
      return arrayIdx[a.id] - arrayIdx[b.id];
    });
  })();

  const assigneesByRole = Object.fromEntries(team.map((g) => [g.role, g.members || []]));

  // ─── Render ───────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="journey-planner-loading">
        <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen p-6 bg-slate-50" data-testid="journey-planner-error">
        <div className="max-w-2xl mx-auto bg-rose-50 border border-rose-200 text-rose-800 p-4 rounded-md">
          <p className="font-semibold">{t('provider.journeyPlanner.loadFailedTitle')}</p>
          <p className="text-sm mt-1">{error}</p>
          <button onClick={load} className="mt-3 text-xs underline">{t('provider.journeyPlanner.retry')}</button>
        </div>
      </div>
    );
  }

  const paymentReceived = !!ctx?.payment?.received;
  const planMaterialized = !!ctx?.plan_materialized;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="coordinator-journey-planner">
      <div className="max-w-[1900px] mx-auto px-3 py-2 space-y-2">
        {/* Header — tight one-line layout */}
        <header
          className="bg-white border border-slate-200 rounded-md px-3 py-1.5 flex flex-wrap items-center gap-2"
          data-testid="planner-header"
        >
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-semibold text-slate-800 leading-tight">
              {t('provider.journeyPlanner.title')}
              <span className="ml-2 text-[11px] font-normal text-slate-500">
                {t('provider.journeyPlanner.caseLabel')} <span className="font-mono">{caseId}</span>
                {ctx?.treatment_slug && <> · <span className="font-mono">{ctx.treatment_slug}</span></>}
                {ctx?.accepted_offer_id && <> · offer <span className="font-mono">{String(ctx.accepted_offer_id).slice(0, 8)}…</span></>}
                {planMaterialized && <> · rev <span className="font-mono">{ctx.applied_revision}</span></>}
                {pendingOps.length > 0 && (
                  <span data-testid="planner-pending-badge" className="ml-2 inline-flex items-center text-[10px] uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5">
                    {pendingOps.length} {t('provider.journeyPlanner.unsaved')}
                  </span>
                )}
              </span>
            </h1>
          </div>
          <div className="flex items-center gap-1.5">
            <select
              data-testid="planner-source-filter"
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className="text-[11px] border border-slate-300 rounded px-1.5 py-0.5 leading-tight"
            >
              <option value="all">{t('provider.journeyPlanner.filterAllSources')}</option>
              <option value="treatment_template">{t('provider.journeyPlanner.filterTemplate')}</option>
              <option value="offer_item">{t('provider.journeyPlanner.filterOffer')}</option>
              <option value="manual">{t('provider.journeyPlanner.filterManual')}</option>
              <option value="campaign">{t('provider.journeyPlanner.filterCampaign')}</option>
            </select>
            <button
              data-testid="planner-add-manual-row"
              onClick={onAddManualRow}
              disabled={!planMaterialized || !paymentReceived}
              className="inline-flex items-center gap-1 text-[11px] bg-slate-900 text-white rounded px-2 py-1 hover:bg-slate-800 disabled:bg-slate-300"
            >
              <Plus className="h-3 w-3" /> {t('provider.journeyPlanner.addRow')}
            </button>
            <button
              data-testid="planner-add-step"
              onClick={onAddManualStep}
              disabled={!planMaterialized || !paymentReceived}
              className="inline-flex items-center gap-1 text-[11px] bg-white border border-slate-300 text-slate-700 rounded px-2 py-1 hover:bg-slate-50 disabled:opacity-50"
            >
              <Plus className="h-3 w-3" /> {t('provider.journeyPlanner.step')}
            </button>
            <button
              data-testid="planner-reload"
              onClick={load}
              className="inline-flex items-center gap-1 text-[11px] bg-white border border-slate-300 text-slate-700 rounded px-2 py-1 hover:bg-slate-50"
              title={t('provider.journeyPlanner.reloadFromServer')}
            >
              <RefreshCw className="h-3 w-3" />
            </button>
            <button
              data-testid="planner-save"
              onClick={onSave}
              disabled={saving || pendingOps.length === 0}
              className="inline-flex items-center gap-1 text-[11px] bg-emerald-600 text-white rounded px-2.5 py-1 hover:bg-emerald-700 disabled:bg-slate-300"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              {t('provider.journeyPlanner.save')}{pendingOps.length > 0 ? ` (${pendingOps.length})` : ''}
            </button>
          </div>
        </header>

        {/* Payment-precondition block */}
        {!paymentReceived && (
          <div
            data-testid="planner-payment-blocked"
            className="bg-rose-50 border border-rose-200 text-rose-900 px-3 py-2 rounded-md flex items-start gap-2"
          >
            <Lock className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <div className="text-xs">
              <p className="font-semibold">{t('provider.journeyPlanner.paymentRequiredTitle')}</p>
              <p className="mt-0.5 text-rose-700">
                {t('provider.journeyPlanner.paymentRequiredBody')}
              </p>
            </div>
          </div>
        )}

        {/* Materialize CTA when no plan yet */}
        {paymentReceived && !planMaterialized && (
          <div
            data-testid="planner-materialize-cta"
            className="bg-white border border-slate-200 rounded-md px-3 py-2 flex items-center justify-between"
          >
            <div>
              <p className="text-xs font-semibold text-slate-800">{t('provider.journeyPlanner.noPlanTitle')}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {t('provider.journeyPlanner.noPlanBodyBase')}
                {ctx?.offer_adjustments_preview?.length ? t('provider.journeyPlanner.noPlanBodyOfferSuffix') : ''}
                {t('provider.journeyPlanner.noPlanBodyTail')}
              </p>
              {!ctx?.template_exists && ctx?.treatment_slug && (
                <p className="text-[11px] text-amber-700 mt-0.5">
                  {t('provider.journeyPlanner.noTemplateWarning', { slug: ctx.treatment_slug })}
                </p>
              )}
            </div>            <button
              data-testid="planner-materialize-btn"
              onClick={materializeNow}
              disabled={materializing || !ctx?.template_exists}
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-xs px-3 py-1.5 rounded hover:bg-slate-800 disabled:bg-slate-300"
            >
              {materializing && <Loader2 className="h-3 w-3 animate-spin" />}
              {t('provider.journeyPlanner.materializePlan')}
            </button>
          </div>
        )}

        {/* E3 — read-only commercial scope of the accepted offer, exactly as
            the offer lifecycle persisted it. No figure is computed here. */}
        {!!ctx?.offer_adjustments_preview?.length && (
          <div
            data-testid="planner-offer-scope"
            className="bg-white border border-slate-200 rounded-md px-3 py-2"
          >
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-xs font-semibold text-slate-800">
                {t('provider.journeyPlanner.offerScopeTitle')}
              </p>
              <p className="text-[10px] text-slate-500">
                {t('provider.journeyPlanner.offerScopeHint')}
              </p>
            </div>
            <ul className="mt-1.5 divide-y divide-slate-100">
              {ctx.offer_adjustments_preview.flatMap((adj) => adj.rows || []).map((row, i) => {
                const c = row.commercial || {};
                return (
                  <li
                    key={row.id || i}
                    data-testid={`planner-offer-scope-row-${i}`}
                    className="py-1 flex items-baseline justify-between gap-3 text-[11px]"
                  >
                    <span className="text-slate-700">
                      {row.name}
                      {c.description && <span className="text-slate-500 ml-1">— {c.description}</span>}
                      {c.ambiguous_legacy_duplicate && (
                        <span className="ml-1.5 text-amber-700">
                          ({t('provider.journeyPlanner.offerScopeAmbiguous')})
                        </span>
                      )}
                    </span>
                    <span className="flex-shrink-0 tabular-nums text-slate-900">
                      {c.classification === 'included'
                        ? <span className="text-slate-500">{t('provider.journeyPlanner.offerScopeIncluded')}</span>
                        : c.unit_price === null || c.unit_price === undefined
                          ? <span className="text-slate-400">{t('provider.journeyPlanner.offerScopeNoPrice')}</span>
                          : `${c.currency || ''} ${c.unit_price}`}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Journey Steps row */}
        {planMaterialized && (
          <div
            data-testid="planner-steps-strip"
            className="bg-white border border-slate-200 rounded-md px-2 py-1 flex flex-wrap items-center gap-1"
          >
            <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">{t('provider.journeyPlanner.stepsLabel')}</span>
            {sortedSteps.map((s, i) => (
              <div
                key={s.id}
                data-testid={`planner-step-${s.id}`}
                className="inline-flex items-center gap-0.5 text-[11px] bg-slate-100 border border-slate-200 rounded pl-1.5 pr-0.5 py-0.5"
              >
                <span className="font-medium text-slate-700">{i + 1}. {s.name}</span>
                <button
                  data-testid={`planner-step-up-${s.id}`}
                  onClick={() => onMoveStep(s.id, 'up')}
                  className="text-slate-400 hover:text-slate-700"
                  title={t('provider.journeyPlanner.moveEarlier')}
                >
                  <ArrowUp className="h-2.5 w-2.5" />
                </button>
                <button
                  data-testid={`planner-step-down-${s.id}`}
                  onClick={() => onMoveStep(s.id, 'down')}
                  className="text-slate-400 hover:text-slate-700"
                  title={t('provider.journeyPlanner.moveLater')}
                >
                  <ArrowDown className="h-2.5 w-2.5" />
                </button>
              </div>
            ))}
            {sortedSteps.length === 0 && (
              <span className="text-[11px] text-slate-400">{t('provider.journeyPlanner.noStepsHint')}</span>
            )}
            {pendingOps.length > 0 && (
              <span className="ml-auto text-[10px] text-slate-500 flex items-center gap-2" data-testid="planner-legend">
                <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-emerald-500"></span>{t('provider.journeyPlanner.legendAdded')}</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-amber-500"></span>{t('provider.journeyPlanner.legendEdited')}</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-rose-500"></span>{t('provider.journeyPlanner.legendDeleted')}</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-indigo-500"></span>{t('provider.journeyPlanner.legendMoved')}</span>
              </span>
            )}
          </div>
        )}

        {/* Grid */}
        {planMaterialized && (
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] border-collapse" data-testid="planner-grid">
                <thead className="bg-slate-100 sticky top-0 z-10">
                  <tr className="text-slate-600 text-left">
                    <th className="px-1.5 py-1 font-medium border-b border-slate-200">{t('provider.journeyPlanner.colSource')}</th>
                    <th className="px-1.5 py-1 font-medium border-b border-slate-200">{t('provider.journeyPlanner.colStep')}</th>
                    <th className="px-1.5 py-1 font-medium border-b border-slate-200">{t('provider.journeyPlanner.colType')}</th>
                    <th className="px-1.5 py-1 font-medium border-b border-slate-200">{t('provider.journeyPlanner.colTitle')}</th>
                    <th className="px-1.5 py-1 font-medium border-b border-slate-200">{t('provider.journeyPlanner.colAction')}</th>
                    <th className="px-1.5 py-1 font-medium border-b border-slate-200">{t('provider.journeyPlanner.colReqBinding')}</th>
                    <th className="px-1.5 py-1 font-medium border-b border-slate-200" title={t('provider.journeyPlanner.colDocTitle')}>{t('provider.journeyPlanner.colDoc')}</th>
                    <th className="px-1.5 py-1 font-medium border-b border-slate-200" title={t('provider.journeyPlanner.colConsentTitle')}>{t('provider.journeyPlanner.colConsent')}</th>
                    <th className="px-1.5 py-1 font-medium border-b border-slate-200">{t('provider.journeyPlanner.colRole')}</th>
                    <th className="px-1.5 py-1 font-medium border-b border-slate-200">{t('provider.journeyPlanner.colAssignee')}</th>
                    <th className="px-1.5 py-1 font-medium border-b border-slate-200" title={t('provider.journeyPlanner.colDueTitle')}>{t('provider.journeyPlanner.colDue')}</th>
                    <th className="px-1.5 py-1 font-medium border-b border-slate-200">{t('provider.journeyPlanner.colPri')}</th>
                    <th className="px-1.5 py-1 font-medium border-b border-slate-200">{t('provider.journeyPlanner.colNotes')}</th>
                    <th className="px-1.5 py-1 font-medium border-b border-slate-200">{t('provider.journeyPlanner.colStatus')}</th>
                    <th className="px-1.5 py-1 font-medium border-b border-slate-200 text-right">{t('provider.journeyPlanner.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r) => {
                    const assigneeOptions = assigneesByRole[r.default_role] || [];
                    const sourceClass = SOURCE_BADGE[r.source] || SOURCE_BADGE.manual;
                    const sourceLabel = SOURCE_LABEL[r.source] || r.source;
                    const state = rowState[r.id];
                    const isDeleted = state === 'deleted';
                    const borderClass =
                      state === 'added'   ? 'border-l-4 border-l-emerald-500'
                    : state === 'edited'  ? 'border-l-4 border-l-amber-500'
                    : state === 'deleted' ? 'border-l-4 border-l-rose-500 bg-rose-50/40'
                    : state === 'moved'   ? 'border-l-4 border-l-indigo-500'
                                          : 'border-l-4 border-l-transparent';
                    const rowOpacity = isDeleted ? 'opacity-60 line-through decoration-rose-400' : '';
                    return (
                      <tr
                        key={r.id}
                        data-testid={`planner-row-${r.id}`}
                        data-pending-state={state || 'clean'}
                        className={`hover:bg-slate-50 border-b border-slate-100 align-middle ${borderClass} ${rowOpacity}`}
                      >
                        <td className="px-1.5 py-0.5 whitespace-nowrap">
                          <span
                            data-testid={`planner-row-source-${r.id}`}
                            className={`inline-block text-[9px] uppercase tracking-wide border rounded px-1.5 py-px ${sourceClass}`}
                          >
                            {sourceLabel}
                          </span>
                          {state && (
                            <span
                              data-testid={`planner-row-state-${r.id}`}
                              className={`ml-1 inline-block text-[9px] uppercase tracking-wide rounded px-1 py-px border ${
                                state === 'added'   ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                state === 'edited'  ? 'bg-amber-50  text-amber-700  border-amber-200'  :
                                state === 'deleted' ? 'bg-rose-50   text-rose-700   border-rose-200'   :
                                                      'bg-indigo-50 text-indigo-700 border-indigo-200'
                              }`}
                            >
                              {state}
                            </span>
                          )}
                        </td>
                        <td className="px-1 py-0.5">
                          <select
                            data-testid={`planner-row-step-${r.id}`}
                            value={r.step_id || ''}
                            disabled={isDeleted}
                            onChange={(e) => onEditRow(r.id, 'step_id', e.target.value)}
                            className="w-full bg-transparent border border-transparent hover:border-slate-200 focus:border-slate-400 rounded px-1 py-0.5"
                          >
                            <option value="">—</option>
                            {sortedSteps.map((s) => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-1 py-0.5">
                          <select
                            data-testid={`planner-row-type-${r.id}`}
                            value={r.kind}
                            disabled={isDeleted}
                            onChange={(e) => onEditRow(r.id, 'kind', e.target.value)}
                            className="w-full bg-transparent border border-transparent hover:border-slate-200 focus:border-slate-400 rounded px-1 py-0.5"
                          >
                            {ROW_TYPE_KEYS.map((rt) => (
                              <option key={rt.value} value={rt.value}>{t(rt.labelKey)}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-1 py-0.5 min-w-[180px]">
                          <input
                            data-testid={`planner-row-title-${r.id}`}
                            value={r.name}
                            disabled={isDeleted}
                            onChange={(e) => onEditRow(r.id, 'name', e.target.value)}
                            className="w-full bg-transparent border border-transparent hover:border-slate-200 focus:border-slate-400 rounded px-1 py-0.5"
                          />
                        </td>
                        <td className="px-1 py-0.5 min-w-[200px]">
                          <input
                            data-testid={`planner-row-action-${r.id}`}
                            value={r.patient_action}
                            disabled={isDeleted}
                            onChange={(e) => onEditRow(r.id, 'patient_action', e.target.value)}
                            className="w-full bg-transparent border border-transparent hover:border-slate-200 focus:border-slate-400 rounded px-1 py-0.5"
                          />
                        </td>
                        <td className="px-1.5 py-0.5 whitespace-nowrap">
                          {r.requirement_id ? (
                            <span
                              data-testid={`planner-row-reqbind-${r.id}`}
                              className="inline-block text-[10px] font-mono bg-sky-50 border border-sky-200 text-sky-800 rounded px-1.5 py-px"
                              title={r.requirement_title_at_freeze || ''}
                            >
                              {String(r.requirement_id).slice(0, 14)}
                              {r.requirement_version ? ` v${r.requirement_version}` : ''}
                            </span>
                          ) : (
                            <span data-testid={`planner-row-reqbind-${r.id}`} className="text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-1 py-0.5 text-center">
                          <input
                            type="checkbox"
                            data-testid={`planner-row-doc-${r.id}`}
                            checked={r.document_required}
                            disabled={isDeleted}
                            onChange={(e) => onEditRow(r.id, 'document_required', e.target.checked)}
                            className="h-3 w-3"
                          />
                        </td>
                        <td className="px-1 py-0.5 text-center">
                          <input
                            type="checkbox"
                            data-testid={`planner-row-consent-${r.id}`}
                            checked={r.consent_required}
                            disabled={isDeleted}
                            onChange={(e) => onEditRow(r.id, 'consent_required', e.target.checked)}
                            className="h-3 w-3"
                          />
                        </td>
                        <td className="px-1 py-0.5">
                          <select
                            data-testid={`planner-row-role-${r.id}`}
                            value={r.default_role || ''}
                            disabled={isDeleted}
                            onChange={(e) => onEditRow(r.id, 'default_role', e.target.value)}
                            className="w-full bg-transparent border border-transparent hover:border-slate-200 focus:border-slate-400 rounded px-1 py-0.5"
                          >
                            <option value="">—</option>
                            {ROLE_KEYS.map((ro) => (
                              <option key={ro.value} value={ro.value}>{t(ro.labelKey)}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-1 py-0.5">
                          <select
                            data-testid={`planner-row-assignee-${r.id}`}
                            value={r.default_assignee || ''}
                            disabled={!r.default_role || isDeleted}
                            onChange={(e) => onEditRow(r.id, 'default_assignee', e.target.value)}
                            className="w-full bg-transparent border border-transparent hover:border-slate-200 focus:border-slate-400 rounded px-1 py-0.5 disabled:bg-slate-50 disabled:text-slate-400"
                          >
                            <option value="">
                              {r.default_role ? '—' : 'pick role first'}
                            </option>
                            {assigneeOptions.map((u) => (
                              <option key={u.user_id} value={u.user_id}>{u.display_name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-1 py-0.5 w-16">
                          <input
                            data-testid={`planner-row-due-${r.id}`}
                            type="number"
                            value={r.due_offset_days}
                            disabled={isDeleted}
                            placeholder={t('provider.journeyPlanner.dueOffsetPlaceholder')}
                            onChange={(e) =>
                              onEditRow(
                                r.id,
                                'due_offset_days',
                                e.target.value === '' ? null : Number(e.target.value),
                              )
                            }
                            className="w-full bg-transparent border border-transparent hover:border-slate-200 focus:border-slate-400 rounded px-1 py-0.5 font-mono text-right"
                          />
                        </td>
                        <td className="px-1 py-0.5">
                          <select
                            data-testid={`planner-row-priority-${r.id}`}
                            value={r.priority}
                            disabled={isDeleted}
                            onChange={(e) => onEditRow(r.id, 'priority', e.target.value)}
                            className="w-full bg-transparent border border-transparent hover:border-slate-200 focus:border-slate-400 rounded px-1 py-0.5"
                          >
                            {PRIORITY_KEYS.map((p) => (
                              <option key={p.value} value={p.value}>{t(p.labelKey)}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-1 py-0.5 min-w-[140px]">
                          <input
                            data-testid={`planner-row-notes-${r.id}`}
                            value={r.notes}
                            disabled={isDeleted}
                            onChange={(e) => onEditRow(r.id, 'notes', e.target.value)}
                            className="w-full bg-transparent border border-transparent hover:border-slate-200 focus:border-slate-400 rounded px-1 py-0.5"
                          />
                        </td>
                        <td className="px-1 py-0.5">
                          <span
                            data-testid={`planner-row-status-${r.id}`}
                            className="text-[10px] uppercase tracking-wider text-slate-500"
                          >
                            {r.satisfaction_status || r.status || t('provider.journeyPlanner.statusDraft')}
                          </span>
                        </td>
                        <td className="px-1 py-0.5 text-right whitespace-nowrap">
                          {isDeleted ? (
                            <button
                              data-testid={`planner-row-undo-${r.id}`}
                              onClick={() => onUndoDelete(r.id)}
                              className="text-[10px] underline text-rose-700 hover:text-rose-900 px-1"
                              title={t('provider.journeyPlanner.undoDelete')}
                            >
                              {t('provider.journeyPlanner.undo')}
                            </button>
                          ) : (
                            <>
                              <button
                                data-testid={`planner-row-up-${r.id}`}
                                onClick={() => onMoveRow(r.id, 'up')}
                                className="text-slate-400 hover:text-slate-800 px-0.5"
                                title={t('provider.journeyPlanner.moveUp')}
                              >
                                <ArrowUp className="h-3 w-3 inline" />
                              </button>
                              <button
                                data-testid={`planner-row-down-${r.id}`}
                                onClick={() => onMoveRow(r.id, 'down')}
                                className="text-slate-400 hover:text-slate-800 px-0.5"
                                title={t('provider.journeyPlanner.moveDown')}
                              >
                                <ArrowDown className="h-3 w-3 inline" />
                              </button>
                              <button
                                data-testid={`planner-row-duplicate-${r.id}`}
                                onClick={() => onDuplicateRow(r.id)}
                                className="text-slate-400 hover:text-slate-800 px-0.5"
                                title={t('provider.journeyPlanner.duplicate')}
                              >
                                <CopyIcon className="h-3 w-3 inline" />
                              </button>
                              <button
                                data-testid={`planner-row-delete-${r.id}`}
                                onClick={() => onDeleteRow(r.id)}
                                className="text-rose-500 hover:text-rose-700 px-0.5"
                                title={t('provider.journeyPlanner.delete')}
                              >
                                <Trash2 className="h-3 w-3 inline" />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {visibleRows.length === 0 && (
                    <tr>
                      <td colSpan={15} className="text-center text-slate-400 py-4 text-[11px]">
                        {t('provider.journeyPlanner.noRowsInView')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Footer crumbs */}
        <div className="text-[10px] text-slate-400 flex flex-wrap gap-3 px-1 pt-1">
          <Link to={`/clinic/cases/${caseId}/workflow`} className="underline">{t('provider.journeyPlanner.backToWorkflow')}</Link>
          <Link to="/clinic/cases" className="underline">{t('provider.journeyPlanner.allCases')}</Link>
          {planMaterialized && (
            <span>
              {t('provider.journeyPlanner.pendingChanges', { count: pendingOps.length })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function stripUiFields(r) {
  // Remove derived/UI-only fields before sending to the backend.
  const {
    modified_by_override,
    satisfaction_status,
    source_offer_id,
    source_campaign_id,
    source_template_id,
    source_override_id,
    ...rest
  } = r;
  return rest;
}
